import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ArticleCardProps {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string;
  category: string;
  categorySlug: string;
  publishDate: string;
  views: number;
  isHot?: boolean;
  isExclusive?: boolean;
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  coverImage,
  category,
  categorySlug,
  publishDate,
  views,
  isHot,
  isExclusive,
  variant = 'default',
}: ArticleCardProps) {
  const { t } = useTranslation();

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            title={title}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
            {title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-400">
            <span>{publishDate}</span>
            <span>·</span>
            <span>{views.toLocaleString()} {t('views')}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'large') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            title={title}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 mb-2">
              {isHot && (
                <span className="px-2 py-0.5 rounded-full bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
                  {t('hot')}
                </span>
              )}
              {isExclusive && (
                <span className="px-2 py-0.5 rounded-full bg-primary-500 text-white text-xs font-medium whitespace-nowrap">
                  {t('exclusive')}
                </span>
              )}
              <span className="px-2 py-0.5 rounded-full bg-white/20 text-white text-xs font-medium whitespace-nowrap">
                {category}
              </span>
            </div>
            <h3 className="text-white font-heading font-bold text-lg leading-snug line-clamp-2 group-hover:text-primary-200 transition-colors">
              {title}
            </h3>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="w-full sm:w-[200px] flex-shrink-0 aspect-[16/10] sm:aspect-auto rounded-lg overflow-hidden">
        <img
          src={coverImage}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          title={title}
        />
      </div>
      <div className="flex-1 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Link
              to={`/category/${categorySlug}`}
              className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              {category}
            </Link>
            {isHot && (
              <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                {t('hot')}
              </span>
            )}
            {isExclusive && (
              <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium whitespace-nowrap">
                {t('exclusive')}
              </span>
            )}
          </div>
          <h3 className="font-heading font-semibold text-foreground-950 text-base leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {title}
          </h3>
          <p className="text-foreground-500 text-sm mt-1.5 line-clamp-2 leading-relaxed hidden sm:block">
            {excerpt}
          </p>
        </div>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {publishDate}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {views.toLocaleString()} {t('views')}
          </span>
        </div>
      </div>
    </Link>
  );
}